/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { storageAccountsSwaggerToStorageAccountStore } from 'src/core/handlers/azure/azureMappers';
import { checkForAzurePermissions } from 'src/core/handlers/azure/utils';
import { getAzureService } from 'src/core/services/azure/azureService';
import { getAzureStorageAccountService } from 'src/core/services/azure/azureStorageAccountService';
import { AzurePermissionsSwagger, AzureStorageAccountSwagger } from 'src/core/services/azure/azureSwaggers';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getNextAbleResults } from 'src/core/utils/batchPromises';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

export type AzureStorageHandlerGroup = ReturnType<typeof createAzureStorageHandlerGroup>;

const sortByName = <T extends { name: string }>(data: T[]) =>
    data.sort((a, b) => (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1));

/**
 * Creates a handler group of functions for Azure blob service to
 * be fired by the dispatcher.
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createAzureStorageHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const azureStorageService = getAzureStorageAccountService();

    /**
     * Loads storage accounts in specific subscription.
     *
     * @param param0.id The id of the subscription to get the accounts for.
     * @param param0.tokenType The token by which the handler will load the accounts for.
     * @param param0.localStore The store to use to store the accounts.
     */
    const getAzureStorageAccountsBySubscriptionId = async ({
        id,
        localStore = rootStore.azureStore.storageAccountStoreHouse
    }: HandlerParams<
        Pick<AzureSubscriptionStore, 'id'> & {
            localStore?: ResourceStoreHouse<AzureStorageAccountStore>;
        }
    >) => {
        await getAzureStorageAccounts({
            localStore,
            storageAccountsGetter: token => azureStorageService.getAzureStorageAccountsBySubscriptionId({ id, token })
        });
    };

    /**
     * Loads storage accounts in specific subscription and region.
     *
     * @param param0.id The id of the subscription to get the accounts for.
     * @param param0.region The name of the region to get the accounts for.
     * @param param0.localStore The store to use to store the accounts.
     */
    const getAzureStorageAccountsBySubscriptionIdAndRegion = async ({
        id,
        region,
        localStore = rootStore.azureStore.storageAccountStoreHouse
    }: HandlerParams<
        Pick<AzureSubscriptionStore, 'id'> & {
            region: string;
            localStore?: ResourceStoreHouse<AzureStorageAccountStore>;
        }
    >) => {
        await getAzureStorageAccounts({
            localStore,
            storageAccountsGetter: token => azureStorageService.getAzureStorageAccountsBySubscriptionId({ id, token, region })
        });
    };

    const getAzureStorageAccounts = async ({
        localStore,
        storageAccountsGetter
    }: {
        localStore?: ResourceStoreHouse<AzureStorageAccountStore>;
        storageAccountsGetter: (token: string) => Promise<AzureStorageAccountSwagger[]>;
    }) => {
        const userToken = rootStore.azureStore.session.userTokens.armToken;

        let userAzureStorageAccountsSwagger: AzureStorageAccountSwagger[] = [];

        localStore.tracker.setState('running');

        userAzureStorageAccountsSwagger = await storageAccountsGetter(userToken);

        const storageAccountStores = userAzureStorageAccountsSwagger
            .map(userStorage => storageAccountsSwaggerToStorageAccountStore(userStorage, 'userToken'))
            .filter((storageAccount, index, arr) => arr.findIndex(s => s.id === storageAccount.id) === index);

        localStore.setItems(sortByName(storageAccountStores));

        localStore.tracker.setState('completed');
    };

    /**
     * Checks for required roles on Storage account.
     *
     * @param param0.id The id of the subscription containing storage account.
     * @param param0.storageAccount The Azure storage account store to check for.
     */
    const checkStorageAccountRequiredRoles = async ({
        id,
        roles,
        storageAccount
    }: HandlerParams<
        Pick<AzureSubscriptionStore, 'id'> & { storageAccount: AzureStorageAccountStore; roles: AzurePermissionsSwagger }
    >) => {
        const tenantToken = rootStore.azureStore.session.userTokens.armToken;

        try {
            const storageAccountPermissions = await getNextAbleResults<'value', AzurePermissionsSwagger>(
                (nextLink: string) =>
                    getAzureService().listPermissionsForAzureStorageAccount({
                        id,
                        nextLink,
                        tenantToken,
                        accountName: storageAccount.name,
                        resourceGroupName: storageAccount.resourceGroupName
                    }),
                'value'
            );

            const storageAccountPermissionsSatisfied = checkForAzurePermissions(storageAccountPermissions, roles);

            if (!storageAccountPermissionsSatisfied) {
                throw new Error('This storage account does not have the required permissions');
            }
        } catch (error) {
            throw error;
        }
    };

    return {
        getAzureStorageAccountsBySubscriptionId,
        getAzureStorageAccountsBySubscriptionIdAndRegion,
        checkStorageAccountRequiredRoles
    };
};
